import React, { useEffect, useMemo, useState } from "react";
import Datatable from "../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../core/utils/getSearchableHeader";
import Footer from "../../core/common/Footer";
import useApi from "../../core/customhook/useApi";
import { displayValue } from "../../core/common/TabelValue";
import { PageBreadcrumb } from "../../core/common/BreadCrumbForm";
import { safeLocaleCompare } from "../../core/methods/sorterMethods";
import { PaginationType } from "../../types/common-type";
import SnipperLoader from "../../core/common/loader/SnipperLoader";
import { PageLoader } from "../../core/common/loader/PageLoader";
import { cellWidth } from "../../core/methods/tableCellWidth";
import { exportAntdTableToExcel } from "../../core/utils/exportExcel";

// breadcrumb data
const breadcrumbItem = [{ label: "Line of Business" }];

const ManagerLOBList = () => {
  const [allItemList, setAllItemList] = useState<any[]>([]); // dummydata_lobData
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 1,
  });

  const { callApi, loading } = useApi<any>(
    "/api/v1/agency/manager-list-of-business",
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const filteredData = useMemo(() => {
    return allItemList?.filter((row) =>
      Object.entries(columnSearch).every(([key, value]) =>
        value
          ? row[key as keyof typeof row]
              ?.toString()
              .toLowerCase()
              .includes(value?.toLowerCase())
          : true,
      ),
    );
  }, [allItemList, columnSearch]);

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },
    {
      title: getSearchableHeader("Account", "project_name", setColumnSearch),
      dataIndex: "project_name",
      render: displayValue,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "project_name"),
    },
    {
      title: getSearchableHeader("Program", "program_name", setColumnSearch),
      dataIndex: "program_name",
      render: displayValue,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "program_name"),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setColumnSearch,
      ),
      dataIndex: "department_name",
      render: displayValue,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "department_name"),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      onCell: () => cellWidth(140),
      render: (isActive: boolean) => {
        const color = isActive ? "#0F9D58" : "#D93025";

        return (
          <span
            className="badge rounded-pill d-inline-flex align-items-center gap-1 px-3 py-2 fw-medium"
            style={{ background: isActive ? "#E6F7ED" : "#FDECEB", color }}
          >
            <span
              className="rounded-circle"
              style={{ width: 6, height: 6, background: color }}
            />
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
      sorter: (a: any, b: any) => Number(a.is_active) - Number(b.is_active),
    },
    // {
    //   title: "Status",
    //   dataIndex: "is_active",
    //   key: "is_active",
    //   align: "center",
    //   width: 150,
    //   render: (status: boolean) => (
    //     <span
    //       style={{
    //         display: "inline-flex",
    //         alignItems: "center",
    //         justifyContent: "center",
    //         gap: "6px",
    //         padding: "6px 14px",
    //         borderRadius: "50px",
    //         backgroundColor: status ? "#E8F8EF" : "#FDECEC",
    //         color: status ? "#198754" : "#DC3545",
    //         border: `1px solid ${status ? "#B7E4C7" : "#F5C2C7"}`,
    //         fontSize: "13px",
    //         fontWeight: 600,
    //         minWidth: "100px",
    //       }}
    //     >
    //       {status ? "Active" : "Inactive"}
    //     </span>
    //   ),
    // },
  ];

  // ======== fetch all managers =========
  const fetchData = async () => {
    try {
      const res = await callApi({
        page: pagination?.current ?? 1,
        limit: pagination?.pageSize ?? 10,
      });
      if (res?.success) {
        const data = res?.data?.rows || [];
        setAllItemList(data);

        setPagination((prev) => ({
          ...prev,
          total: res?.data?.pagination?.totalRecords || 0,
        }));
      }
    } catch (error: any) {
      console.error(error?.response);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.current, pagination?.pageSize]);

  // handleTableChange
  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };
  // ======== / fetch all managers =========

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Line_Of_Business_Report",
      sheetName: "Line Of Business",
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {loading ? (
          <PageLoader />
        ) : (
          <>
            {/* PageBreadcrumb */}
            <PageBreadcrumb
              title="Line of Business"
              breadcrumbs={breadcrumbItem}
            />
            {/* /PageBreadcrumb */}

            {/* Table  */}
            <div className="card mt-3">
              <div className="d-flex justify-content-end pe-2 py-2 pb-0">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleDownloadExcel}
                  disabled={loading}
                >
                  <i className="ti ti-download" /> Export to Excel
                </button>
              </div>
              <Datatable
                dataSource={filteredData}
                columns={columns}
                fixedHeader
                tableHeight={650}
                Selection={false}
                pagination={pagination}
                handleTableChange={handleTableChange}
              />
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ManagerLOBList;
